import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { BackupStatus } from '@repo/api-gw-sdk';

import { inventoryItemFields } from '@/app/(dashboard)/inventory/inventoryItemFields';
import { FieldsSection } from '@/app/(dashboard)/jobs/[tab]/fieldsSection';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import {
  clusterName,
  dbEngine,
  id,
  namespaceName,
  resourceGroupName,
  resourceId,
  resourceType,
  sourceRegion,
  tags,
  VolumesCloudProviderMapping,
} from '@/components/queryBuilder/properties';
import { PropertyGroups } from '@/components/queryBuilder/propertyGroups';
import { SecurityScanCard } from '@/components/security/securityScanCard';
import { hasSecurityIssue } from '@/components/security/securityUtils';
import { useFields } from '@/components/table/hooks';
import { groupActionsByLevel } from '@/data/inventory/actions';
import { ResourceTypes } from '@/data/inventory/resourceTypes';

import { BackupStatusCard } from './backupStatusCard';
import {
  BigQueryDatasetMetadata,
  BigQueryTablesSection,
  BigQueryViewsSection,
} from './bigQuerySection';
import { Classifications } from './classifications';
import { DatabaseSection } from './databaseSection';
import {
  DynamoDBBackupConfigurationSection,
  DynamoDBPropertiesSection,
} from './dynamoDBSection';
import { EKSNamespaceSection } from './eksNamespaceSection';
import { FSxSection } from './fsxSection';
import { InstanceActionsBar } from './instanceActionsBar';
import { JobsSection } from './jobsSection';
import { RedshiftSection } from './redshiftSection';
import { VolumeSection } from './volumeSection';
import { VolumesSection } from './volumesSection';

import type { InstancePanelProps } from '../../../instancePanel';
import { SidePanelPropsTable } from '../../../sidePanelPropsTable';

const rootLevelProperties = [
  resourceType.name,
  resourceId.name,
  id.name,
  resourceGroupName.name,
  sourceRegion.name,
  dbEngine.name,
  clusterName.name,
  namespaceName.name,
  tags.name,
];

export const OverviewTab = (props: InstancePanelProps) => {
  const { entity } = props;
  const fields = useFields(inventoryItemFields, entity, props.onEntityChange);
  const actions = groupActionsByLevel(entity.backupStatus, props.actions);

  return (
    <Box className='pb-[40px]' overflow='auto'>
      <Box className='pt-[40px] pr-[24px]'>
        <CopyToClipboardWrapper textToCopy={entity.resourceName}>
          <Typography
            className='pl-[40px]'
            variant='h2'
            data-testid='side-panel-resource-name'
          >
            {entity.resourceName}
          </Typography>
        </CopyToClipboardWrapper>
        <SidePanelPropsTable className='mt-[24px] mb-[24px]'>
          <tbody>
            {fields
              .filter((field) => rootLevelProperties.includes(field.id))
              .map((x) => (
                <tr key={x.id}>
                  <td className='props-col'>{x.title}</td>
                  <td className='break-all'>{x.Component}</td>
                </tr>
              ))}
          </tbody>
        </SidePanelPropsTable>
      </Box>
      <InstanceActionsBar entity={entity} actions={actions} />
      <Stack gap='24px' paddingX='40px' margin={'40px 0 24px'}>
        {hasSecurityIssue(entity) && (
          <SecurityScanCard
            resource={entity}
            onExclusionChanged={props.onEntityChange}
          />
        )}
        <BackupStatusCard
          entity={entity}
          backupStatus={entity.backupStatus}
          onEntityChange={props.onEntityChange}
          actions={actions}
        />
      </Stack>

      {entity.backupStatus !== BackupStatus.InitialClassification && (
        <Accordion disableGutters>
          <AccordionSummary data-testid='classifications'>
            {PropertyGroups.classifications.title}
          </AccordionSummary>
          <AccordionDetails>
            <Classifications
              entity={entity}
              onEntityChange={props.onEntityChange}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <FieldsSection
        fields={fields.filter(
          (x) =>
            x.group === 'backup-details' && !rootLevelProperties.includes(x.id)
        )}
        title={PropertyGroups['backup-details'].title}
        columnSpace='medium'
      />
      <FieldsSection
        fields={fields.filter(
          (x) =>
            x.group === 'source-location' && !rootLevelProperties.includes(x.id)
        )}
        title={PropertyGroups['source-location'].title}
        columnSpace='medium'
      />
      {!!(
        entity.resourceProperties?.awsEc2?.volumes?.length ||
        entity.resourceProperties?.gcpVmResource?.disks?.length
      ) && (
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary>
            {VolumesCloudProviderMapping[entity.cloudProvider] || 'Volumes'}
          </AccordionSummary>
          <AccordionDetails className='px-0'>
            <VolumesSection resource={entity} />
          </AccordionDetails>
        </Accordion>
      )}
      {!!(
        entity.resourceProperties?.awsEbsVolume ||
        entity.resourceProperties?.gcpDisk
      ) && (
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary>{'Properties'}</AccordionSummary>
          <AccordionDetails className='px-0'>
            <VolumeSection resource={entity} />
          </AccordionDetails>
        </Accordion>
      )}
      {(entity.resourceProperties?.awsEksNamespace ||
        entity.resourceProperties?.azureAksNamespace ||
        entity.resourceProperties?.gcpGkeNamespace) && (
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary>
            {ResourceTypes[entity.resourceType].displayName}
          </AccordionSummary>
          <AccordionDetails className='px-0'>
            <EKSNamespaceSection resource={entity} />
          </AccordionDetails>
        </Accordion>
      )}
      {entity.resourceProperties?.awsRds && (
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary>{'Database properties'}</AccordionSummary>
          <AccordionDetails className='px-0'>
            <DatabaseSection resource={entity} />
          </AccordionDetails>
        </Accordion>
      )}
      {entity.resourceProperties?.awsFsx && (
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary>{'File system properties'}</AccordionSummary>
          <AccordionDetails sx={{ padding: '24px 0' }}>
            <FSxSection resource={entity} />
          </AccordionDetails>
        </Accordion>
      )}
      {entity.resourceProperties?.awsRedshift && (
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary>{'Cluster properties'}</AccordionSummary>
          <AccordionDetails sx={{ padding: '24px 0' }}>
            <RedshiftSection resource={entity} />
          </AccordionDetails>
        </Accordion>
      )}
      {entity.resourceProperties?.awsDynamoDb && (
        <>
          <Accordion disableGutters defaultExpanded={false}>
            <AccordionSummary>{'Table properties'}</AccordionSummary>
            <AccordionDetails className='px-0'>
              <DynamoDBPropertiesSection resource={entity} />
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters defaultExpanded={false}>
            <AccordionSummary>{'Table backup configuration'}</AccordionSummary>
            <AccordionDetails className='px-0'>
              <DynamoDBBackupConfigurationSection resource={entity} />
            </AccordionDetails>
          </Accordion>
        </>
      )}
      {entity.resourceProperties?.gcpBigQuery && (
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary>{'Dataset properties'}</AccordionSummary>
          <AccordionDetails className='px-0'>
            <BigQueryDatasetMetadata resource={entity} />
          </AccordionDetails>
        </Accordion>
      )}
      {entity.resourceProperties?.gcpBigQuery?.tables &&
        entity.resourceProperties.gcpBigQuery.tables.length > 0 && (
          <Accordion disableGutters defaultExpanded={false}>
            <AccordionSummary>{'Tables'}</AccordionSummary>
            <AccordionDetails className='px-0'>
              <BigQueryTablesSection resource={entity} />
            </AccordionDetails>
          </Accordion>
        )}
      {entity.resourceProperties?.gcpBigQuery &&
        ((entity.resourceProperties.gcpBigQuery.views &&
          entity.resourceProperties.gcpBigQuery.views.length > 0) ||
          (entity.resourceProperties.gcpBigQuery.materializedViews &&
            entity.resourceProperties.gcpBigQuery.materializedViews.length >
              0) ||
          (entity.resourceProperties.gcpBigQuery.routines &&
            entity.resourceProperties.gcpBigQuery.routines.length > 0)) && (
          <Accordion disableGutters defaultExpanded={false}>
            <AccordionSummary>{'Views'}</AccordionSummary>
            <AccordionDetails className='px-0'>
              <BigQueryViewsSection resource={entity} />
            </AccordionDetails>
          </Accordion>
        )}
      <Accordion disableGutters defaultExpanded={false}>
        <AccordionSummary>{'Jobs'}</AccordionSummary>
        <AccordionDetails className='px-0'>
          <JobsSection resource={entity} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
